/* ============================================================
   KP REMARKETING — assets/kp-remarketing.js, loaded with defer AFTER
   theme-analytics.js. Replaces the remarketing half of Simprosys.

   Remarketing is the one part of the stack that CANNOT be server-side:
   audience membership requires Google's tag setting a cookie in the
   visitor's real browser. There is no API that adds a user to a remarketing
   list. So this file exists, and the AW- id below is necessarily visible in
   page source. Accepted trade: an AW- id is low-value to attackers (a faked
   conversion with no real gclid is discarded by Ads, and joining an audience
   requires actually running this tag in a browser).

   How bots are kept out of the audiences — three layers, first two already
   live in this theme:
     1. layout/theme.liquid's script gate holds any googletagmanager.com
        script until a TRUSTED interaction, and never releases for the
        1280x1200 bot fingerprint.
     2. This file additionally does not even create the gtag script until it
        has seen its own isTrusted event — belt and braces, so it stays safe
        even if the theme gate is ever removed.
     3. Consent: nothing loads unless Shopify's Customer Privacy API allows
        marketing.
   A bot that loads pages and leaves never causes gtag to exist on the page,
   so it can never enter an audience.

   ITEM IDS ARE SKUS — merchant decision, 5 Aug 2026. The Merchant Center
   feed (5757080530) keys items by SKU, so dynamic remarketing must send the
   SKU, never the numeric Shopify product/variant id. "Product SKU" here means
   the selected-or-first-available variant's SKU, which theme.liquid publishes
   as body[data-product-sku] and theme-analytics.js carries on every bus event
   as product_sku. An event with no SKU is skipped rather than sent with a
   wrong id — a mismatched id silently poisons the audience feed match.
   ============================================================ */
(function () {
  var ADS_ID = 'AW-768431092'; /* <-- Google Ads conversion id */
  var storeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function measurementAllowed() {
    return !window.kpTrackingPolicy || window.kpTrackingPolicy.allowed();
  }

  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
    } catch (e) {}
    return true; /* no consent platform present -> Shopify has allowed pixels to run */
  }

  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    } catch (e) {}
    return true;
  }

  function consentSignals() {
    var ad = marketingAllowed() ? 'granted' : 'denied';
    return {
      ad_storage: ad,
      ad_user_data: ad,
      ad_personalization: ad,
      analytics_storage: analyticsAllowed() ? 'granted' : 'denied'
    };
  }

  function activeCurrency() {
    try { return String(window.Shopify.currency.active || 'GBP'); } catch (e) { return 'GBP'; }
  }

  var gaCid = '';
  var gaSid = '';
  function syncGAIdsToCart() {
    if (document.querySelector('[data-kp-finder-market-gate]')) return;
    if (!gaCid || !gaSid) return;
    // Queued with the other page-load cart writers (kp-cart-attributes).
    if (window.kpCartAttributes) { window.kpCartAttributes({ _kp_ga_cid: gaCid, _kp_ga_sid: gaSid }); return; }
    fetch(storeRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _kp_ga_cid: gaCid, _kp_ga_sid: gaSid } }),
      keepalive: true
    }).then(function (r) {
      if (!r.ok) throw new Error('cart sync ' + r.status);
    }).catch(function () {});
  }

  window.addEventListener('pageshow', function(e) {
    if (!e.persisted) return;
    try {
      var cidCookie = document.cookie.match(/(?:^|;\s*)_kp_cid=([^;]+)/);
      var sidCookie = document.cookie.match(/(?:^|;\s*)_kp_sid=([^;]+)/);
      gaCid = cidCookie ? decodeURIComponent(cidCookie[1]) : '';
      gaSid = sidCookie ? decodeURIComponent(sidCookie[1]) : '';
      syncGAIdsToCart();
    } catch (_) {}
  });

  /* GA4 and Ads are loaded SEPARATELY as of 7 Aug 2026.
     ------------------------------------------------------------------
     They were one gated function. That made GA4's session-opening
     page_view wait for a trusted interaction — but GA4 fixes a session's
     traffic source on the FIRST page_view, and the Worker's server-side
     hit had already opened a sourceless session by then. Visitors who
     bounced, or who clicked straight through, never loaded gtag at all.
     Result: 276 of ~350 sessions "(not set)" from 5 Aug, and every
     purchase landing on a session GA4 could not join, because
     _kp_ga_cid was never written so the beacon fell back to its own id.

     Same trap the theme documented for Web Pixels Manager on 4 Aug:
     an attribution-bearing tag cannot be interaction-gated. The bot
     protection that motivated the gate is preserved exactly — audience
     membership comes from the ADS tag, and that is still gated. */
  var gtagReady = false;
  function ensureGA4() {
    if (gtagReady) return;
    if (!measurementAllowed()) return;
    if (looksAutomated()) return;      /* bots still never load gtag */
    if (!analyticsAllowed()) return;
    gtagReady = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', consentSignals());
    window.gtag('js', new Date());
    /* GA4 session bootstrap — added 6 Aug 2026.
       ----------------------------------------------------------------------
       This is the ONLY way GA4 gets a real traffic source. Measurement
       Protocol inherits session attribution from a browser-created session and
       cannot create one: proven across ten payload variants on both
       /mp/collect and /g/collect, and stated in Google's own docs ("the same
       session attributes ... as online events from the same session"). With no
       browser tag, 5-6 Aug ran at 100% "(not set)" against 1-5% while a
       browser tag existed.

       send_page_view is deliberately LEFT AT ITS DEFAULT (true): this
       page_view is the event that creates the attributed session. The Worker
       no longer sends page_view to GA4, so there is no double count.

       Configured on the SAME gtag instance as Ads, so gtag.js is downloaded
       once rather than twice.

       Consequence, stated plainly: the measurement id is now readable in page
       source, and hits go from the browser straight to google-analytics.com,
       independently of the collector. The shared measurement policy checks the
       Worker decision before this config call; it does not intercept forged
       requests sent straight to Google. Ads remains interaction-gated. */
    /* SESSION-START MUST CARRY THE AD URL (21 Aug 2026). GA4 takes session
       source/medium from the session-opening hit's page_location. gtag often
       configures AFTER the ad landing: consent arrives late, or the visitor
       has navigated, so the opening hit went out with a clean URL and 17 of
       19 Google ad sessions that day stored as Direct / "(not set)" / "(data
       not available)" while gtag was working perfectly. The beacon already
       persists the true first-touch landing URL (query string included) in
       localStorage; if the CURRENT url carries no campaign markers but a
       fresh (<30 min) stored touch does, hand that landing to gtag so the
       session opens attributed. url_passthrough additionally carries ad click
       ids across navigations while ad_storage is denied (Consent Mode). */
    /* SERVER-ONLY GA4 (17 Oct 2026, merchant-approved). No GA4 measurement id
       lives in the browser any more: the old one was readable here, and anyone
       could post fake ("ghost") hits to it straight to Google. Page views and
       sessions now reach GA4 only through the Worker (/kpa/collect), after the
       bot filters, on a stream whose id exists only as a Worker secret.
       gtag.js still loads for Google Ads; ga_live (set on its onload) still
       tells the Worker that this browser genuinely loaded Google's script.
       The cart carries the beacon's own ids so a verified purchase joins the
       same GA4 user and session the Worker opened. */
    try {
      var kpCidM = document.cookie.match(/(?:^|;\s*)_kp_cid=([^;]+)/);
      var kpSidM = document.cookie.match(/(?:^|;\s*)_kp_sid=([^;]+)/);
      gaCid = kpCidM ? decodeURIComponent(kpCidM[1]) : '';
      gaSid = kpSidM ? decodeURIComponent(kpSidM[1]) : '';
      syncGAIdsToCart();
    } catch (e) {}
    var g = document.createElement('script');
    g.async = true;
    g.src = 'https://www.googletagmanager.com/gtag/js?id=' + ADS_ID;
    /* ga_live truth: only a LOADED gtag.js can send hits. Blocked script =
       no flag = the beacon reports ga_live 0 = the Worker opens the session
       server-side with the real source instead of trusting a ghost. */
    g.onload = function () { try { window.__kpGaLive = 1; } catch (e) {} };
    document.head.appendChild(g);
  }

  var loaded = false;
  function ensureAds() {
    if (loaded) return;
    ensureGA4();                      /* guarantees gtag.js + dataLayer exist */
    if (!gtagReady) return;
    loaded = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    /* Suppress the Ads config's automatic page view; fireForPage emits the
       appropriate Ads event. GA4 page views belong to ensureGA4's gtag config. */
    /* gtag.js itself was already downloaded by ensureGA4(); this only adds
       the Ads config to the same instance, so the library is fetched once.
       The gtag document submit listener (conversion linker form decoration)
       is kept off the Add to basket tap by the product form itself, which
       stops the submit event after cancelling it (theme-global.js). */
    window.gtag('config', ADS_ID, { send_page_view: false, url_passthrough: true });
  }

  function fireForPage() {
    var b = document.body;
    if (!b) return;
    var pageType = b.dataset.pageType || '';
    var sku = b.dataset.productSku || '';
    var price = parseFloat(b.dataset.productPrice || '0') || 0;
    if (pageType === 'product' && sku) {
      window.gtag('event', 'view_item', {
        send_to: ADS_ID,
        value: price,
        currency: activeCurrency(),
        items: [{ id: sku, price: price, google_business_vertical: 'retail' }]
      });
    } else {
      /* Non-product pages still tag the visitor for standard remarketing. */
      window.gtag('event', 'page_view', { send_to: ADS_ID });
    }
  }

  /* Cart adds carry the successful Shopify response's SKU, price and request
     quantity on PDPs, recommendations and collection quick-adds alike.
     Items without a SKU remain excluded rather than using a numeric ID. */
  document.addEventListener('kp:analytics', function (e) {
    if (!loaded) return;
    var d = e && e.detail;
    if (!d || d.event !== 'cart_add_success' || !d.product_sku) return;
    window.gtag('event', 'add_to_cart', {
      send_to: ADS_ID,
      value: (parseFloat(d.product_price || '0') || 0) * Math.max(1, Number(d.quantity) || 1),
      currency: activeCurrency(),
      items: [{ id: d.product_sku, quantity: Math.max(1, Number(d.quantity) || 1), price: parseFloat(d.product_price || '0') || 0, google_business_vertical: 'retail' }]
    });
  });

  /* Released by a TRUSTED interaction ONLY — tap, click, wheel, key.
     ----------------------------------------------------------------------
     The 2.5s dwell release that lived here was removed on 7 Aug 2026 at the
     merchant's explicit instruction. The dwell existed so a visitor who lands,
     reads and leaves without interacting still produced a GA4 session; the
     accepted cost of removing it is that those visitors are now invisible to
     GA4 (their page_view never fires). The merchant chose this trade because
     the dwell also released gtag for anything that merely keeps the page open
     2.5s — Lighthouse audits and patient headless bots included — which both
     tanked the PageSpeed score and let the 614-events-in-3-days botnet create
     GA4 sessions on a timer.

     Bounces are NOT fully lost: kp-analytics-beacon.js has its own gate and
     the Cloudflare Worker still logs the visit to D1, so the merchant's own
     dashboard sees non-interacting visitors even though GA4 does not.

     Do not re-add a timer here without reading docs/performance-testing.md. */
  /* No 'scroll' (15 Oct 2026): window.scrollTo() fires a trusted scroll and the
     proxy fleet used it. A person's scroll starts with touchstart, wheel,
     mousedown (scrollbar) or keydown, all still listed. */
  var EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'wheel'];


  /* ── automation gate ──────────────────────────────────────
     Added 7 Aug 2026. The dwell gate below does NOT keep bots out: 614 bot
     events reached kp-analytics-beacon.js in three days, and the beacon uses
     the same 2.5s dwell — so every one of them sat on the page for longer than
     that. 263 were signals_swRender, i.e. headless browsers. Dwell only stops
     curl and python-requests.

     That mattered here more than anywhere else, because gtag talks DIRECTLY to
     Google. The Cloudflare Worker filters bots out of Measurement Protocol,
     but it never sees a gtag hit — so every one of those bots was creating a
     real GA4 session by a route nothing was guarding.

     Same signals kp-analytics-beacon.js already computes. Conservative: a false
     positive costs one session of tracking, a false negative costs the accuracy
     of the whole property. */
  function looksAutomated() {
    try {
      if (navigator.webdriver === true) return true;
      if (!screen.width || !screen.height) return true;
      if (!window.outerWidth) return true;
      if (!navigator.languages || !navigator.languages.length) return true;
      if (navigator.hardwareConcurrency === 0) return true;
      var ua = navigator.userAgent || '';
      if (/iPhone|iPad|Android|Mobile/i.test(ua) && !(navigator.maxTouchPoints > 0)) return true;
      /* Software WebGL renderer — the single strongest signal in our data. */
      // Reuse the document's renderer probe across tracking clients and inputs.
      if (window.KPBrowserSignals) return window.KPBrowserSignals.webgl().software;
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (gl) {
        var dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          var r = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '');
          if (/swiftshader|llvmpipe|mesa offscreen/i.test(r)) return true;
        }
      }
    } catch (e) { /* a browser too old for these checks is not a bot */ }
    return false;
  }

  /* INP (17 Oct 2026). The first trusted tap used to run go() inside its own
     pointerdown: gtag's Ads config plus the page event, 106–137 ms at 4x CPU
     with every tracker on, so the screen did not answer the shopper's first
     tap until gtag had finished. The tap is still what releases Ads; the work
     now runs after that tap has painted, and the page event in a task of its
     own. 100 ms covers a hidden tab, where animation frames do not run. */
  function afterPaint(fn) {
    var done = false;
    function run() { if (!done) { done = true; fn(); } }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () { window.setTimeout(run, 0); });
    }
    window.setTimeout(run, 100);
  }

  var humanSeen = false;
  var pageEventSent = false;
  function sendPageEvent() {
    if (pageEventSent || !loaded) return;
    pageEventSent = true;
    fireForPage();
  }
  function go() {
    if (loaded) return;
    if (!measurementAllowed()) return;
    if (looksAutomated()) return;   /* never load gtag for automation */
    if (!marketingAllowed()) return; /* keep waiting; consent may arrive later */
    ensureAds();
    /* Analytics consent can be denied while marketing consent is granted.
       ensureGA4 then waits for the consent update; do not remove the trusted
       interaction listeners or call gtag until it has actually initialized. */
    if (!loaded) return;
    EVENTS.forEach(function (n) { window.removeEventListener(n, release, true); });
    window.setTimeout(sendPageEvent, 0);
  }
  /* Leaving mid-tap (17 Oct 2026): since Ads starts after the tap has painted,
     a first tap on a link can open the next page before go() runs. On pagehide
     start Ads now (gtag.js is normally downloaded already) and send the page
     event in this same task, so the visit still joins the audience and the
     click id is kept. Every later call is a no-op, including a bfcache return. */
  window.addEventListener('pagehide', function () {
    if (humanSeen && !loaded) go();
    sendPageEvent();
  });
  var goQueued = false;
  function release(ev) {
    if (!ev || !ev.isTrusted) return;
    humanSeen = true;
    if (goQueued || loaded) return;
    goQueued = true;
    afterPaint(function () { goQueued = false; go(); });
  }
  EVENTS.forEach(function (n) { window.addEventListener(n, release, true); });

  function consentChanged() {
    if (gtagReady) window.gtag('consent', 'update', consentSignals());
    else ensureGA4();
    if (humanSeen) go();
  }
  document.addEventListener('visitorConsentCollected', consentChanged);
  window.addEventListener('visitorConsentCollected', consentChanged);

  /* Preserve the landing session without a click/dwell gate. The shared edge
     decision is bounded to 1.2s and does not delay page or cart controls. */
  if (window.kpTrackingPolicy) {
    window.kpTrackingPolicy.whenReady(function (allowed) {
      if (!allowed) return;
      ensureGA4();
      if (humanSeen) go();
    });
  } else ensureGA4();
})();
